import type { MaterialItemFragment } from "~/gql/queries/material.generated";
import type { MaterialRequirementBasis } from "~produce/helpers/materialRequirementBasisText";

import { GenericSelectData } from "~/components/select/SelectGeneric";
import { LocationDocument, MaterialVendorData } from "~/gql/graphqlBase.generated";
import { ymdToStartOfDayIso } from "~/helpers/timestampFormat";
import wsId from "~/helpers/wsId";
import { isoWeekOf, mondayOfIsoWeek, utcDateToYmd, ymdToUtcDate } from "~produce/inventory/hardgoods/helpers/readyWeek";
import { type PoCreateWizardDraft } from "~produce/purchasing/orders/create/helpers/poCreateWizard";
import { hasCompleteVendorPackAssignment } from "~produce/purchasing/orders/helpers/vendorUom";
import { type DraftLineItem, type DraftLineJobShare } from "~produce/purchasing/schedule/helpers/lineItemDiff";

export const GENERATE_PO_PARAM = "generate";

export interface GeneratePoSourceRow {
	available: number | null;
	eventDetailId: string;
	itemName: string;
	jobNumber?: number | null;
	jobQuantity: number;
	materialItemId: string;
	multiple: number | null;
	productionJobId: string;
	requiredQuantity: number | null;
	siteLocationId: string | null;
	triggerDate: string | null;
	unitLabel: string | null;
}

export interface GeneratePoSpanHints {
	siteCount: number;
	weekCount: number;
}

export interface GeneratePoShortfall {
	quantity: number;
	row: GeneratePoSourceRow;
}

const QUANTITY_EPSILON = 1e-9;
const SHARE_SCALE = 10_000;

export interface GeneratePoLineOptions {
	combineLines?: boolean;
}

const roundToShareDecimals = (quantity: number): number => Math.round(quantity * SHARE_SCALE) / SHARE_SCALE;

const compareEarliestNeededFirstUndatedLast = (left: GeneratePoSourceRow, right: GeneratePoSourceRow): number => {
	if (left.triggerDate === right.triggerDate) return 0;
	if (left.triggerDate === null) return 1;
	if (right.triggerDate === null) return -1;
	return left.triggerDate < right.triggerDate ? -1 : 1;
};

const compareLatestJobFirstUndatedLast = (left: GeneratePoSourceRow, right: GeneratePoSourceRow): number => {
	if (left.triggerDate === right.triggerDate) return 0;
	if (left.triggerDate === null) return 1;
	if (right.triggerDate === null) return -1;
	return left.triggerDate < right.triggerDate ? 1 : -1;
};

const computeItemSiteDeficit = (rows: readonly GeneratePoSourceRow[]): number =>
	Math.max(0, ...rows.map((row) => -(row.available ?? 0)));

const hasAvailableFigure = (row: GeneratePoSourceRow): boolean => row.available !== null && Boolean(row.siteLocationId);

export const findGeneratePoUnknownRows = (sourceRows: readonly GeneratePoSourceRow[]): GeneratePoSourceRow[] =>
	sourceRows.filter((row) => !hasAvailableFigure(row));

export type GeneratePoNotice = "noShortfall" | "unknownShortfall";

export const selectGeneratePoNotices = (shortfallRowCount: number, unknownRowCount: number): GeneratePoNotice[] => {
	if (unknownRowCount > 0) return ["unknownShortfall"];
	return shortfallRowCount === 0 ? ["noShortfall"] : [];
};

export const computeGeneratePoShortfalls = (sourceRows: readonly GeneratePoSourceRow[]): GeneratePoShortfall[] => {
	const knownRows = sourceRows.filter(hasAvailableFigure);
	const rowsByItemAndSite = new Map<string, GeneratePoSourceRow[]>();
	for (const row of knownRows) {
		const key = `${row.materialItemId}|${row.siteLocationId}`;
		rowsByItemAndSite.set(key, [...(rowsByItemAndSite.get(key) ?? []), row]);
	}
	const shortfalls = new Map<GeneratePoSourceRow, number>();
	for (const rows of rowsByItemAndSite.values()) {
		let remaining = computeItemSiteDeficit(rows);
		for (const row of [...rows].sort(compareLatestJobFirstUndatedLast)) {
			if (remaining <= QUANTITY_EPSILON) break;
			const quantity = Math.min(Math.max(row.requiredQuantity ?? 0, 0), remaining);
			shortfalls.set(row, quantity);
			remaining -= quantity;
		}
	}
	return knownRows
		.map((row) => ({ quantity: shortfalls.get(row) ?? 0, row }))
		.filter((shortfall) => shortfall.quantity > QUANTITY_EPSILON);
};

export const findGeneratePoShortfallRows = (sourceRows: readonly GeneratePoSourceRow[]): GeneratePoSourceRow[] =>
	computeGeneratePoShortfalls(sourceRows).map(({ row }) => row);

const defaultVendorFor = (
	material: Pick<MaterialItemFragment, "vendors"> | undefined
): MaterialVendorData | undefined => material?.vendors?.find((vendor) => vendor.isDefault);

export const resolveVendorFlag = (
	material: Pick<MaterialItemFragment, "vendors"> | undefined,
	poVendorId: string | null
): DraftLineItem["vendorFlag"] => {
	const defaultVendor = defaultVendorFor(material);
	if (!defaultVendor) return "noVendor";
	return defaultVendor.vendorId === poVendorId ? undefined : "vendorMismatch";
};

const findDominantVendorId = (
	rows: readonly GeneratePoSourceRow[],
	materialsById: ReadonlyMap<string, MaterialItemFragment>
): string | null => {
	const counts = new Map<string, number>();
	for (const row of rows) {
		const vendorId = defaultVendorFor(materialsById.get(row.materialItemId))?.vendorId;
		if (!vendorId) continue;
		counts.set(vendorId, (counts.get(vendorId) ?? 0) + 1);
	}
	let dominantVendorId: string | null = null;
	let dominantCount = 0;
	for (const [vendorId, count] of counts) {
		if (count > dominantCount) {
			dominantVendorId = vendorId;
			dominantCount = count;
		}
	}
	return dominantVendorId;
};

const findVendorName = (materials: readonly MaterialItemFragment[], vendorId: string): string | undefined =>
	materials.flatMap((material) => material.vendors ?? []).find((vendor) => vendor.vendorId === vendorId)?.vendorName;

const draftLineBasisOf = (row: GeneratePoSourceRow): MaterialRequirementBasis | undefined =>
	row.multiple === null || row.requiredQuantity === null
		? undefined
		: {
				containers: row.jobQuantity,
				multiple: row.multiple,
				required: row.requiredQuantity,
				unitLabel: row.unitLabel
			};

const buildDraftLine = (
	{ quantity, row }: GeneratePoShortfall,
	lineNumber: number,
	material: MaterialItemFragment | undefined,
	poVendorId: string | null
): DraftLineItem => {
	return {
		erpJobId: "",
		eventDetailId: row.eventDetailId,
		isCostOverridden: false,
		isDeleted: false,
		isDirty: false,
		isNew: true,
		itemName: material?.name ?? row.itemName,
		lineItemId: wsId(),
		lineNumber,
		materialItemErpId: material?.erpId ?? "",
		materialItemId: row.materialItemId,
		productionJobId: row.productionJobId,
		productionJobNumber: row.jobNumber ?? null,
		quantityOrdered: Math.ceil(quantity - QUANTITY_EPSILON),
		requiredBasis: draftLineBasisOf(row),
		unitCost: null,
		vendorFlag: resolveVendorFlag(material, poVendorId)
	};
};

const packSizeOf = (material: MaterialItemFragment | undefined, poVendorId: string | null): number | null => {
	if (!poVendorId) return null;
	const assignment = material?.vendors?.find((vendor) => vendor.vendorId === poVendorId);
	return hasCompleteVendorPackAssignment(assignment) ? assignment.unitsPerUom : null;
};

const roundUpOnce = (total: number, pack: number | null): number => {
	const roundedTotal = roundToShareDecimals(total);
	if (pack === null) return Math.ceil(roundedTotal - QUANTITY_EPSILON);
	return roundToShareDecimals(Math.ceil(roundedTotal / pack - QUANTITY_EPSILON) * pack);
};

const shareOf = ({ quantity, row }: GeneratePoShortfall): DraftLineJobShare | null => {
	const rounded = roundToShareDecimals(quantity);
	if (rounded <= 0) return null;
	return { eventDetailId: row.eventDetailId, productionJobId: row.productionJobId, quantity: rounded };
};

const buildOneCombinedLine = (
	group: readonly GeneratePoShortfall[],
	lineNumber: number,
	material: MaterialItemFragment | undefined,
	poVendorId: string | null
): DraftLineItem | null => {
	const ordered = [...group].sort((left, right) => compareEarliestNeededFirstUndatedLast(left.row, right.row));
	const jobShares = ordered.flatMap((shortfall) => {
		const share = shareOf(shortfall);
		return share ? [share] : [];
	});
	if (jobShares.length === 0) return null;
	const total = roundToShareDecimals(jobShares.reduce((sum, share) => sum + share.quantity, 0));
	const first = ordered[0].row;
	return {
		erpJobId: "",
		isCostOverridden: false,
		isDeleted: false,
		isDirty: false,
		isNew: true,
		itemName: material?.name ?? first.itemName,
		jobShares,
		lineItemId: wsId(),
		lineNumber,
		materialItemErpId: material?.erpId ?? "",
		materialItemId: first.materialItemId,
		productionJobNumber: null,
		quantityOrdered: roundUpOnce(total, packSizeOf(material, poVendorId)),
		unitCost: null,
		vendorFlag: resolveVendorFlag(material, poVendorId)
	};
};

export const buildCombinedDraftLines = (
	shortfalls: readonly GeneratePoShortfall[],
	materialsById: ReadonlyMap<string, MaterialItemFragment>,
	poVendorId: string | null
): DraftLineItem[] => {
	const groups = new Map<string, GeneratePoShortfall[]>();
	for (const shortfall of shortfalls) {
		const key = `${shortfall.row.materialItemId}|${shortfall.row.siteLocationId}`;
		groups.set(key, [...(groups.get(key) ?? []), shortfall]);
	}
	return [...groups.values()].flatMap((group, index) => {
		const line = buildOneCombinedLine(group, index + 1, materialsById.get(group[0].row.materialItemId), poVendorId);
		return line ? [line] : [];
	});
};

const linesFromShortfalls = (
	shortfalls: readonly GeneratePoShortfall[],
	materialsById: ReadonlyMap<string, MaterialItemFragment>,
	poVendorId: string | null,
	combineLines: boolean
): DraftLineItem[] =>
	combineLines
		? buildCombinedDraftLines(shortfalls, materialsById, poVendorId)
		: shortfalls.map((shortfall, index) =>
				buildDraftLine(shortfall, index + 1, materialsById.get(shortfall.row.materialItemId), poVendorId)
			);

export const rebuildGeneratePoLines = (
	sourceRows: readonly GeneratePoSourceRow[],
	allMaterialItems: readonly MaterialItemFragment[],
	poVendorId: string | null,
	options: GeneratePoLineOptions = {}
): DraftLineItem[] =>
	linesFromShortfalls(
		computeGeneratePoShortfalls(sourceRows),
		new Map(allMaterialItems.map((material) => [material.id, material])),
		poVendorId,
		Boolean(options.combineLines)
	);

const lineWasEdited = (current: DraftLineItem, generated: DraftLineItem): boolean =>
	current.isDeleted ||
	current.quantityOrdered !== generated.quantityOrdered ||
	(current.unitCost ?? null) !== (generated.unitCost ?? null) ||
	current.isCostOverridden !== generated.isCostOverridden ||
	current.materialItemId !== generated.materialItemId ||
	current.itemName !== generated.itemName;

export const countEditedGeneratePoLines = (
	current: readonly DraftLineItem[],
	generated: readonly DraftLineItem[]
): number => {
	const generatedById = new Map(generated.map((line) => [line.lineItemId, line]));
	const currentIds = new Set(current.map((line) => line.lineItemId));
	let edited = 0;
	for (const line of current) {
		const original = generatedById.get(line.lineItemId);
		if (!original) {
			if (!line.isDeleted) edited += 1;
			continue;
		}
		if (lineWasEdited(line, original)) edited += 1;
	}
	for (const original of generated) {
		if (!currentIds.has(original.lineItemId)) edited += 1;
	}
	return edited;
};

const DATE_PART_LENGTH = 10;

const weekKeyOf = (row: GeneratePoSourceRow): string | null => {
	const triggerDay = ymdToUtcDate(row.triggerDate?.slice(0, DATE_PART_LENGTH) ?? "");
	if (!triggerDay) return null;
	const { week, year } = isoWeekOf(triggerDay);
	return utcDateToYmd(mondayOfIsoWeek(year, week));
};

const siteKeyOf = (row: GeneratePoSourceRow): string | null => row.siteLocationId || null;

const distinctKeys = (keys: readonly (string | null)[]): Set<string> =>
	new Set(keys.filter((key): key is string => key !== null));

const agreedKey = (keys: readonly (string | null)[]): string | null => {
	const distinct = distinctKeys(keys);
	const everyRowHasKey = keys.every((key) => key !== null);
	return everyRowHasKey && distinct.size === 1 ? [...distinct][0] : null;
};

const spanCount = (keys: readonly (string | null)[]): number => {
	const { size } = distinctKeys(keys);
	return size > 1 ? size : 0;
};

export const findAgreedGeneratePoSiteId = (sourceRows: readonly GeneratePoSourceRow[]): string | null =>
	agreedKey(findGeneratePoShortfallRows(sourceRows).map(siteKeyOf));

const findAgreedGeneratePoWeek = (sourceRows: readonly GeneratePoSourceRow[]): string | null =>
	agreedKey(findGeneratePoShortfallRows(sourceRows).map(weekKeyOf));

export const summarizeGeneratePoSpans = (sourceRows: readonly GeneratePoSourceRow[]): GeneratePoSpanHints => {
	const shortfallRows = findGeneratePoShortfallRows(sourceRows);
	return { siteCount: spanCount(shortfallRows.map(siteKeyOf)), weekCount: spanCount(shortfallRows.map(weekKeyOf)) };
};

export const buildGeneratePoDraft = (
	sourceRows: readonly GeneratePoSourceRow[],
	allMaterialItems: readonly MaterialItemFragment[],
	agreedSite: LocationDocument | null = null,
	options: GeneratePoLineOptions = {}
): PoCreateWizardDraft => {
	const materialsById = new Map(allMaterialItems.map((material) => [material.id, material]));
	const shortfalls = computeGeneratePoShortfalls(sourceRows);
	const shortfallRows = shortfalls.map(({ row }) => row);
	const vendorId = findDominantVendorId(shortfallRows, materialsById);
	const vendorName = vendorId ? findVendorName(allMaterialItems, vendorId) : undefined;
	const vendor: GenericSelectData | null = vendorId && vendorName ? { code: vendorId, name: vendorName } : null;

	const lines = linesFromShortfalls(shortfalls, materialsById, vendorId, Boolean(options.combineLines));

	const agreedWeek = findAgreedGeneratePoWeek(sourceRows);
	const needByDate = agreedWeek ? ymdToStartOfDayIso(agreedWeek) : null;

	return { lines, needByDate, site: agreedSite, vendor };
};

export interface GeneratePoVendorDraft {
	draft: PoCreateWizardDraft;
	/** Stable per draft (vendor and ship week); a vendor can have several drafts. */
	key: string;
	vendor: GenericSelectData;
	/** Monday of the ship week as YYYY-MM-DD; null for rows with no trigger date. */
	weekKey: string | null;
}

export interface GeneratePoLeftOutItem {
	itemName: string;
	materialItemId: string;
}

export interface GeneratePoVendorPlan {
	leftOutItems: GeneratePoLeftOutItem[];
	vendorDrafts: GeneratePoVendorDraft[];
}

const compareWeeksUndatedLast = (left: string | null, right: string | null): number => {
	if (left === right) return 0;
	if (left === null) return 1;
	if (right === null) return -1;
	return left.localeCompare(right);
};

const compareDrafts = (left: GeneratePoVendorDraft, right: GeneratePoVendorDraft): number =>
	left.vendor.name.localeCompare(right.vendor.name) ||
	left.vendor.code.localeCompare(right.vendor.code) ||
	compareWeeksUndatedLast(left.weekKey, right.weekKey);

interface VendorWeekGroup {
	shortfalls: GeneratePoShortfall[];
	vendorId: string;
	vendorName: string;
	weekKey: string | null;
}

const draftKeyOf = (vendorId: string, weekKey: string | null): string => `${vendorId}|${weekKey ?? "none"}`;

const groupShortfallsByVendorAndWeek = (
	shortfalls: readonly GeneratePoShortfall[],
	materialsById: ReadonlyMap<string, MaterialItemFragment>
): {
	groups: Map<string, VendorWeekGroup>;
	withoutVendor: GeneratePoShortfall[];
} => {
	const groups = new Map<string, VendorWeekGroup>();
	const withoutVendor: GeneratePoShortfall[] = [];
	for (const shortfall of shortfalls) {
		const defaultVendor = defaultVendorFor(materialsById.get(shortfall.row.materialItemId));
		if (!defaultVendor) {
			withoutVendor.push(shortfall);
			continue;
		}
		const weekKey = weekKeyOf(shortfall.row);
		const key = draftKeyOf(defaultVendor.vendorId, weekKey);
		const group = groups.get(key) ?? {
			shortfalls: [],
			vendorId: defaultVendor.vendorId,
			vendorName: defaultVendor.vendorName,
			weekKey
		};
		group.shortfalls.push(shortfall);
		groups.set(key, group);
	}
	return { groups, withoutVendor };
};

const distinctLeftOutItems = (
	shortfalls: readonly GeneratePoShortfall[],
	materialsById: ReadonlyMap<string, MaterialItemFragment>
): GeneratePoLeftOutItem[] => {
	const itemsById = new Map<string, GeneratePoLeftOutItem>();
	for (const { row } of shortfalls) {
		if (!itemsById.has(row.materialItemId))
			itemsById.set(row.materialItemId, {
				itemName: materialsById.get(row.materialItemId)?.name ?? row.itemName,
				materialItemId: row.materialItemId
			});
	}
	return [...itemsById.values()];
};

export const buildGeneratePoVendorPlan = (
	sourceRows: readonly GeneratePoSourceRow[],
	allMaterialItems: readonly MaterialItemFragment[],
	agreedSite: LocationDocument | null = null,
	options: GeneratePoLineOptions = {}
): GeneratePoVendorPlan => {
	const materialsById = new Map(allMaterialItems.map((material) => [material.id, material]));
	const { groups, withoutVendor } = groupShortfallsByVendorAndWeek(
		computeGeneratePoShortfalls(sourceRows),
		materialsById
	);
	const vendorDrafts = [...groups.entries()].map(([key, group]): GeneratePoVendorDraft => {
		const vendor = { code: group.vendorId, name: group.vendorName };
		return {
			draft: {
				lines: linesFromShortfalls(group.shortfalls, materialsById, group.vendorId, Boolean(options.combineLines)),
				needByDate: group.weekKey ? ymdToStartOfDayIso(group.weekKey) : null,
				site: agreedSite,
				vendor
			},
			key,
			vendor,
			weekKey: group.weekKey
		};
	});
	return {
		leftOutItems: distinctLeftOutItems(withoutVendor, materialsById),
		vendorDrafts: vendorDrafts.sort(compareDrafts)
	};
};

export default buildGeneratePoDraft;
